import { useMutation } from "@tanstack/react-query";

import {
  trackAiImageRefinementCalled,
  trackAiImageRefinementFailed,
  trackAiImageRefinementGenerated,
  trackImageEditToolAiRefinementCalled,
  trackImageEditToolAiRefinementFailed,
  trackImageEditToolAiRefinementGenerated
} from "@design-lab/shared/lib/analytics";
import api from "common/utils/api";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";
import type { aiImages } from "common/utils/models";
import { AI_REFINE_TIMEOUT } from "common/upload-constants";
import { useTokenManagement } from "../model/tokenManagement";

type RefineParams = aiImages.refine.WeaveRequest | aiImages.refine.ArtworkRequest;

function isArtworkRefine(params: RefineParams): params is aiImages.refine.ArtworkRequest {
  return "dtfArtworkId" in params;
}

function getTrackingId(params: RefineParams): number {
  return isArtworkRefine(params) ? Number(params.dtfArtworkId) : Number(params.aiImageVersionId);
}

const trackRefinementCalled = (params: RefineParams): void => {
  const id = getTrackingId(params);
  const { refinementPrompt } = params;

  if (isArtworkRefine(params)) {
    trackImageEditToolAiRefinementCalled(id, refinementPrompt);
  } else {
    trackAiImageRefinementCalled(id, refinementPrompt);
  }
};

const trackRefinementGenerated = (params: RefineParams): void => {
  const id = getTrackingId(params);
  const { refinementPrompt } = params;

  if (isArtworkRefine(params)) {
    trackImageEditToolAiRefinementGenerated(id, refinementPrompt);
  } else {
    trackAiImageRefinementGenerated(id, refinementPrompt);
  }
};

const trackRefinementFailed = (params: RefineParams, errorMessage: string): void => {
  const id = getTrackingId(params);
  const { refinementPrompt } = params;

  if (isArtworkRefine(params)) {
    trackImageEditToolAiRefinementFailed(id, refinementPrompt, errorMessage);
  } else {
    trackAiImageRefinementFailed(id, refinementPrompt, errorMessage);
  }
};

const refineAiImage = async (params: RefineParams): Promise<aiImages.refine.Response> => {
  if (isArtworkRefine(params)) {
    const formData = new FormData();
    formData.append("refinement_prompt", params.refinementPrompt);
    formData.append("image", params.image);
    formData.append("dtf_artwork_id", params.dtfArtworkId);

    const data = await api.aiImages.refine(formData, {
      raw: true,
      useAxiosForRequest: true,
      headers: { "Content-Type": undefined },
      timeout: AI_REFINE_TIMEOUT
    });
    return objNamesToCamelCase(data);
  }
  const data = await api.aiImages.refine(
    objNamesToSnakeCase({
      refinementPrompt: params.refinementPrompt,
      inputImageUrl: params.inputImageUrl,
      aiImageVersionId: params.aiImageVersionId
    }),
    {
      timeout: AI_REFINE_TIMEOUT,
      useAxiosForRequest: true
    }
  );
  return objNamesToCamelCase(data);
};

export function useRefineAiImage() {
  const { updateAiCreditsBalance } = useTokenManagement();

  return useMutation({
    mutationFn: refineAiImage,
    onMutate: trackRefinementCalled,
    onSuccess: (data, variables) => {
      updateAiCreditsBalance(data.remainingCredits);
      trackRefinementGenerated(variables);
    },
    onError: (error: any, variables) => {
      const errorMessage = error?.message || error?.response?.data?.error || "Unknown error";

      if (error?.response) {
        const response = objNamesToCamelCase(error.response);
        if (response.remainingCredits !== undefined) {
          updateAiCreditsBalance(response.remainingCredits);
        }
      }

      trackRefinementFailed(variables, errorMessage);
    }
  });
}
